import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import * as Fonts from "../../components/Fonts";
import Input from "../../components/Form/Input/input";
import { Form, FormGroup } from "../../components/Form/styles";
import SaveBar from "../../components/SaveBar";
import { notifySaveError } from "../../components/SaveErrorNotice";
import { SubHeader } from "../../components/SubHeader";
import constants from "../../constants";
import { useAppState } from "../../contexts/App";
import { useSite } from "../../contexts/Site";
import { PAGES_PATH, withPage } from "../../hooks/CurrentPage";
import { useQueryUrl } from "../../hooks/QueryUrl";
import {
  getPages,
  MAX_PAGES,
  PageError,
  SLUG_MAX,
  slugify,
  TITLE_MAX,
  validatePage,
  withPages,
} from "../../model/pages";
import { SitePage } from "../../model/site";
import { api } from "../../services/api";
import { uuidv4 } from "../../utils/uuid";
import { Card, Container, Section } from "../Settings/styles";
import { Error } from "./styles";

/** Creates a page (no `?id`) or edits one: title, address (from the title until edited by hand), SEO. */
export const PageFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();
  const editingId = useQueryUrl().get("id");
  const pages = getPages(site);
  const editing = pages.find(({ id }) => id === editingId);
  const prefix = "pages.editor.pages";
  // back to the Keepo's pages
  const list = PAGES_PATH;

  const [title, setTitle] = useState(editing?.title ?? "");
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [slugTouched, setSlugTouched] = useState(!!editing);
  const [error, setError] = useState<PageError | null>(null);
  const hasUnsavedChanges = () => title !== (editing?.title ?? "") || slug !== (editing?.slug ?? "");

  // a new page past the limit (the form opened by its address): back to the list, which says so
  if (!editing && pages.length >= MAX_PAGES) {
    return <Navigate to={list} replace />;
  }

  const onSave = () => {
    const problem = validatePage({ title, slug }, pages, editing?.id);
    setError(problem);
    if (problem) return;

    // what else the page already has (its sections, menu, SEO) stays as it is
    const page: SitePage = {
      ...editing,
      id: editing?.id ?? uuidv4(),
      title: title.trim(),
      slug,
      sections: editing?.sections ?? [],
      showInMenu: editing?.showInMenu ?? true,
    };
    // the Keepo's first page: with the menu off nothing leads to it, so it comes on (and the person can turn it off)
    const next = withPages(site, editing ? pages.map(p => (p.id === page.id ? page : p)) : [...pages, page]);

    setSite(next);
    setAppState({ ...appState, editingSiteModified: true });
    api.saveDraft(appState.editingSiteId as string, next).catch(notifySaveError);
    // a new page: straight into its structure (sections); edited details: back to the list
    navigate(editing ? list : withPage("/editor/sections", page.id));
  };

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.form.${editing ? "editTitle" : "newTitle"}`)}
        crumbs={[{ label: t(`${prefix}.title`), to: list }]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <Form
        onSubmit={e => {
          e.preventDefault();
          onSave();
        }}>
        <Section>
          <Fonts.Label>{t(`${prefix}.sections.details`)}</Fonts.Label>
          <Card>
            <FormGroup>
              <Input
                id="page-title"
                type="text"
                label={t(`${prefix}.form.title`)}
                value={title}
                maxLength={TITLE_MAX}
                onChange={e => {
                  setTitle(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value));
                }}
              />
              {error === "titleRequired" && <Error role="alert">{t(`${prefix}.form.errors.${error}`)}</Error>}
            </FormGroup>
            <FormGroup>
              <Input
                id="page-slug"
                type="text"
                autoCapitalize="none"
                label={t(`${prefix}.form.slug`)}
                value={slug}
                maxLength={SLUG_MAX}
                hint={t(`${prefix}.form.slugHint`, {
                  // the Keepo's own address; a generic one while it has none yet
                  address: site.url ? `${site.url}.${constants.nameSite}` : t(`${prefix}.form.slugHintAddress`),
                  slug: slug || "contato",
                })}
                onChange={e => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase().trim());
                }}
              />
              {error && error !== "titleRequired" && <Error role="alert">{t(`${prefix}.form.errors.${error}`)}</Error>}
            </FormGroup>
          </Card>
        </Section>

        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" />
      </Form>
      {/* fixed at the bottom, as on every editing screen */}
      <SaveBar sticky onSave={onSave} hasUnsavedChanges={hasUnsavedChanges} />
    </Container>
  );
};

export default PageFormPage;
